import React from 'react';

/** `public/assets/red potions` (vida) ou `blue potions` (XP). */
export type PotionGridVariant = 'health' | 'experience';

export type PotionGridSize = 'small' | 'medium' | 'large';

/** Map inventory XP potion buff percent to sprite size (30/70/100 → P/M/G). */
export function xpPotionSpriteSizeFromBonusPercent(percent: number): PotionGridSize {
  switch (percent) {
    case 30:
      return 'small';
    case 70:
      return 'medium';
    case 100:
      return 'large';
    default:
      return 'medium';
  }
}

export interface InventoryPotionGridMeta {
  variant: PotionGridVariant;
  size: PotionGridSize;
}

const healthKeyToMeta: Record<string, InventoryPotionGridMeta> = {
  SmallHealthPotion: { variant: 'health', size: 'small' },
  MediumHealthPotion: { variant: 'health', size: 'medium' },
  LargeHealthPotion: { variant: 'health', size: 'large' },
};

const experienceKeyToMeta: Record<string, InventoryPotionGridMeta> = {
  SmallExperiencePotion: { variant: 'experience', size: 'small' },
  MediumExperiencePotion: { variant: 'experience', size: 'medium' },
  LargeExperiencePotion: { variant: 'experience', size: 'large' },
};

/** PNG 48×48, grade 3×3, 8 frames; mesmo layout que red potions. */
export function inventoryPotionGridMeta(itemTypeKey: string): InventoryPotionGridMeta | null {
  return healthKeyToMeta[itemTypeKey] ?? experienceKeyToMeta[itemTypeKey] ?? null;
}

const variantFolder: Record<PotionGridVariant, string> = {
  health: 'red potions',
  experience: 'blue potions',
};

export function getPotionGridSpriteSrc(variant: PotionGridVariant, size: PotionGridSize): string {
  const segments = ['assets', variantFolder[variant], `${size}.png`];
  const path = segments.map(encodeURIComponent).join('/');
  const prefix = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '');
  return prefix ? `${prefix}/${path}` : `/${path}`;
}

export interface InventoryPotionSpriteProps {
  variant: PotionGridVariant;
  size: PotionGridSize;
  frameDisplayPx?: number;
  className?: string;
}

const InventoryPotionSprite: React.FC<InventoryPotionSpriteProps> = ({
  variant,
  size,
  frameDisplayPx = 48,
  className = '',
}) => {
  const src = getPotionGridSpriteSrc(variant, size);
  return (
    <span
      className={`inventory-potion-grid-sprite inventory-potion-grid-sprite--grid3x3 ${className}`.trim()}
      style={
        {
          width: frameDisplayPx,
          height: frameDisplayPx,
          backgroundImage: `url(${src})`,
          ['--inv-potion-fp' as string]: `${frameDisplayPx}px`,
        } as React.CSSProperties
      }
      role="img"
      aria-hidden
    />
  );
};

export default InventoryPotionSprite;
